import React, { memo, useEffect, useState } from 'react';
import { Skeleton } from '@material-ui/lab';
import { encodeModule } from 'helpers/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'services/request';
import { courseConfigAdd } from 'store/actions/courseConfig';
import { tabsNamesLoaded, tabsOpened } from 'store/actions/tabs';
import { cleanupFavorites } from 'store/actions/favorites';
import { ICourseConfig } from 'store/reducers/courseConfig';
import { IAppState } from 'store/reducers/rootReducer';
import { IMenuItem, IMenuItemApi } from 'types/menu';

// Import components
import { GlobalModulesSection } from './menu/GlobalModulesSection';
import { LanguageSection } from './menu/LanguageSection';
import { ModuleDropdown } from './menu/ModuleDropdown';
import { FavoritesSection } from './menu/FavoritesSection';
import { FavoritesDropdown } from './menu/FavoritesDropdown';

// Import utilities and constants
import { GroupedItems, processMenuItems, generateMenuItemId } from './menu/utils';

// Import styles
import { Wrapper, SkeletonWrapper, Divider, LanguagesSection } from './menu/styles';

export const LeftMenuNav: React.FC = memo(() => {
  const [groupedItems, setGroupedItems] = useState<GroupedItems>({ globalModules: [], languages: {} });
  const [loading, setLoading] = useState(true);
  const [collapsedByLanguage, setCollapsedByLanguage] = useState<Record<string, boolean>>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentModules, setCurrentModules] = useState<IMenuItem[]>([]);
  const [currentCourse, setCurrentCourse] = useState<{ language: string; courseName: string } | null>(null);
  const [favoritesAnchorEl, setFavoritesAnchorEl] = useState<HTMLElement | null>(null);
  const dispatch = useDispatch();
  const favouritesTabs = useSelector((state: IAppState) => state.favouritesTabs);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearTimeout((window as any).popperTimeout);
      clearTimeout((window as any).favoritesTimeout);
    };
  }, []);

  // Close favorites dropdown when no favorites remain
  useEffect(() => {
    if (Object.keys(favouritesTabs.items).length === 0) {
      setFavoritesAnchorEl(null);
    }
  }, [favouritesTabs.items]);

  useEffect(() => {
    (async () => {
      try {
        const [res, courseConfigs] = await Promise.all([
          get<IMenuItemApi[]>(`permissions-tree`),
          get<ICourseConfig[]>(`courses/config`),
        ]);

        // If the requests failed due to unauthenticated (or other error), they will not be arrays.
        // In that case, skip processing to avoid wiping favourites on session expiry.
        if (!Array.isArray(res) || !Array.isArray(courseConfigs)) {
          return;
        }

        const processed = processMenuItems(res);
        setGroupedItems(processed);

        // Initialize collapsed state per language (default collapsed)
        const initial: Record<string, boolean> = { ...collapsedByLanguage };
        Object.keys(processed.languages).forEach((lng) => {
          if (typeof initial[lng] === 'undefined') initial[lng] = true;
        });
        setCollapsedByLanguage(initial);

        courseConfigs.forEach((courseConfig) => dispatch(courseConfigAdd({ [courseConfig.id]: courseConfig })));

        // Build module names and collect valid module IDs
        const moduleNames: { [key: string]: string } = {};
        const validModuleIds: string[] = [];

        // Add global modules (but don't add to validModuleIds - they can't be favorited)
        processed.globalModules.forEach((item) => {
          moduleNames[encodeModule({ ...item, refId: item.ref })] = item.path.join(' - ');
        });

        // Add language courses and modules
        Object.values(processed.languages).forEach((courses) => {
          courses.forEach(({ course, modules }) => {
            // Add the course (but don't add to validModuleIds - they can't be favorited)
            moduleNames[encodeModule({ ...course, refId: course.ref })] = course.path.join(' - ');

            // Add all modules - only these can be favorited
            modules.forEach((module) => {
              const moduleId = generateMenuItemId(module);
              validModuleIds.push(moduleId);
              moduleNames[encodeModule({ ...module, refId: module.ref })] = module.path.join(' - ');
            });
          });
        });

        dispatch(tabsNamesLoaded(moduleNames));

        // Clean up favorites that are no longer in permissions
        dispatch(cleanupFavorites(validModuleIds));
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch]);

  const handleToggleLanguage = (language: string) => {
    setCollapsedByLanguage((prev) => ({ ...prev, [language]: !prev[language] }));
  };

  const handleItemClick = (item: IMenuItem) => {
    dispatch(
      tabsOpened({
        refId: item.ref,
        courseId: item.courseId,
      }),
    );
  };

  const handleCourseHover = (
    e: React.MouseEvent<HTMLDivElement>,
    course: IMenuItem,
    modules: IMenuItem[],
    language: string,
  ) => {
    // Clear any pending hide timeout
    clearTimeout((window as any).popperTimeout);

    // Update immediately to prevent flashing
    const element = e.currentTarget;
    setAnchorEl(element);
    setCurrentModules(modules);
    setCurrentCourse({ language, courseName: course.title });
  };

  const handleCourseLeave = () => {
    // Hide after delay
    (window as any).popperTimeout = setTimeout(() => {
      setAnchorEl(null);
      setCurrentModules([]);
      setCurrentCourse(null);
    }, 200);
  };

  const handleDropdownEnter = () => {
    // Keep popper open when hovering over it
    clearTimeout((window as any).popperTimeout);
  };

  const handleDropdownLeave = () => {
    // Close popper when leaving
    (window as any).popperTimeout = setTimeout(() => {
      setAnchorEl(null);
      setCurrentModules([]);
      setCurrentCourse(null);
    }, 200);
  };

  // Favorites handlers
  const handleFavoritesHover = (e: React.MouseEvent<HTMLDivElement>) => {
    clearTimeout((window as any).favoritesTimeout);
    setFavoritesAnchorEl(e.currentTarget);
  };

  const handleFavoritesLeave = () => {
    (window as any).favoritesTimeout = setTimeout(() => {
      setFavoritesAnchorEl(null);
    }, 200);
  };

  const handleFavoritesDropdownEnter = () => {
    clearTimeout((window as any).favoritesTimeout);
  };

  const handleFavoritesDropdownLeave = () => {
    (window as any).favoritesTimeout = setTimeout(() => {
      setFavoritesAnchorEl(null);
    }, 200);
  };

  if (loading) {
    return (
      <SkeletonWrapper>
        <Skeleton variant="text" width="80%" height={36} style={{ marginBottom: 8 }} />
        <Skeleton variant="text" width="70%" height={36} style={{ marginBottom: 8 }} />
        <Skeleton variant="text" width="75%" height={36} style={{ marginBottom: 8 }} />
        <Skeleton variant="text" width="65%" height={36} style={{ marginBottom: 8 }} />
      </SkeletonWrapper>
    );
  }

  return (
    <Wrapper>
      <GlobalModulesSection modules={groupedItems.globalModules} onItemClick={handleItemClick} />

      <Divider />

      <FavoritesSection onHover={handleFavoritesHover} onLeave={handleFavoritesLeave} />

      <LanguagesSection>
        {Object.keys(groupedItems.languages)
          .sort((a, b) => a.localeCompare(b))
          .map((language) => (
            <LanguageSection
              key={language}
              language={language}
              courses={groupedItems.languages[language] || []}
              onCourseHover={handleCourseHover}
              onCourseLeave={handleCourseLeave}
              collapsed={collapsedByLanguage[language] ?? true}
              onToggle={handleToggleLanguage}
            />
          ))}
      </LanguagesSection>

      <ModuleDropdown
        anchorEl={anchorEl}
        modules={currentModules}
        courseInfo={currentCourse}
        onModuleClick={handleItemClick}
        onMouseEnter={handleDropdownEnter}
        onMouseLeave={handleDropdownLeave}
      />

      <FavoritesDropdown
        anchorEl={favoritesAnchorEl}
        favorites={favouritesTabs}
        onModuleClick={handleItemClick}
        onMouseEnter={handleFavoritesDropdownEnter}
        onMouseLeave={handleFavoritesDropdownLeave}
      />
    </Wrapper>
  );
});
